/**
 * Copyright (C) 2023 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */

import { StyledEngineProvider, ThemeProvider } from '@mui/material';
import React, { useEffect } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import {
  selectIsInitialTerminalsRequestFinished,
  selectMapDimensions,
  selectMapPosition,
  selectMapSettingsLabelsTileState,
  selectMapSettingsSatViewTileState,
  selectOverlaysCurrentContext,
  selectPlatformListDrawerOpen,
} from '../../../store';
import { FLEET_MAP_ID, TRANSPORT_MONITORING_MAP_ID, setMapHidden } from '../../../store/maps/actions';
import { OverlaysContextsEnum } from '../../../store/maps/reducer';
import ErrorBoundary from '../../common/layout/ErrorBoundary';
import { MapContext } from '../../common/map/BaseMapComponent';
import BaseMapContainer from '../../common/map/BaseMapContainer';
import TailTagOverlaysPanel from '../../common/map_overlays/TailTagOverlaysPanel';
import MapSettingsPanel from '../../common/map_settings/MapSettingsPanel';
import appTheme from '../../common/theme/appTheme.json';
import HubsContainer from '../../fleet/Hubs/HubsContainer';
import WeatherOverlay from '../../fleet/WeatherOverlay';
import { ToggleTileStateEnum } from '../../materialui_wrappers/ToggleTile';
import GroundStationsContainer from './GroundStations/GroundStationsContainer';
import { switchOverlaysContext } from '../../../store/actions';
import AddTailTagsToMap from '../../fleet/AddTailTagsToMap';

import TransportMonitoringMap from './TransportMonitoringMap';
import OverlaysPanel from '../../common/map_overlays/OverlaysPanel';
import RulerToolDistanceSummary from '../../common/map_overlays/RulerToolDistanceSummary';

export const VEHICLE_DRAWER_WIDTH = 439;

const TransportMonitoringMapContainer = () => {
  // 3rd Party Hooks
  const dispatch = useDispatch();

  // Selectors
  const mapSettingsLabelsTileState = useSelector(
    selectMapSettingsLabelsTileState(TRANSPORT_MONITORING_MAP_ID),
    shallowEqual,
  );

  // Labels for Transport Monitoring map
  const mapSettingsSatViewTileState = useSelector(
    selectMapSettingsSatViewTileState(TRANSPORT_MONITORING_MAP_ID),
    shallowEqual,
  );

  // Sat View for Transport Monitoring map
  const mapDimensions = useSelector(selectMapDimensions(TRANSPORT_MONITORING_MAP_ID), shallowEqual); // Dimensions override for Transport Monitoring map
  const mapPosition = useSelector(selectMapPosition(TRANSPORT_MONITORING_MAP_ID), shallowEqual); // Position override for Transport Monitoring map
  const terminalListDrawerOpen = useSelector(selectPlatformListDrawerOpen, shallowEqual);
  const isInitialTerminalsRequestFinished = useSelector(selectIsInitialTerminalsRequestFinished, shallowEqual);
  const isFullWidth = !isInitialTerminalsRequestFinished;

  // determine width
  let leftMargin = terminalListDrawerOpen ? `${VEHICLE_DRAWER_WIDTH}px` : '32px';

  let mapWidth = isFullWidth ? '100%' : `calc(100% - ${leftMargin})`;

  // change width if overriden in Redux store
  mapWidth = mapDimensions?.width ? mapDimensions.width : mapWidth;

  // determine height
  let mapHeight = 'calc(100vh - 104px)';

  // change height if overriden in Redux store
  mapHeight = mapDimensions?.height ? mapDimensions.height : mapHeight;

  // determine left px offset
  let mapLeft = isFullWidth || mapDimensions ? '0px' : leftMargin;
  mapLeft = mapPosition?.left ? mapPosition.left + 'px' : mapLeft;

  // determine top px offset
  const mapTop = mapPosition?.top ? mapPosition.top + 'px' : undefined;

  useEffect(() => {
    // Ensure the map is shown and the context is updated
    dispatch(switchOverlaysContext(OverlaysContextsEnum.TRANSPORT_MONITORING));

    // hide the fleet/FTV view map in the case of refresh on any TM page
    dispatch(setMapHidden(FLEET_MAP_ID)(true));
  }, [dispatch]);
  useEffect(() => {
    dispatch(switchOverlaysContext(OverlaysContextsEnum.TRANSPORT_MONITORING));
  });
  const overlaysCurrentContext = useSelector(selectOverlaysCurrentContext, shallowEqual);
  useEffect(() => {}, [overlaysCurrentContext]);
  return (
    <BaseMapContainer
      id={TRANSPORT_MONITORING_MAP_ID}
      width={mapWidth}
      height={mapHeight}
      mapLabelsEnabled={mapSettingsLabelsTileState === ToggleTileStateEnum.ON}
      mapSatelliteViewEnabled={mapSettingsSatViewTileState === ToggleTileStateEnum.ON}
      left={mapLeft}
      top={mapTop}
      persistZoom
    >
      <MapContext.Consumer>
        {map => (
          <React.Fragment>
            <StyledEngineProvider injectFirst>
              <ThemeProvider theme={appTheme}>
                {overlaysCurrentContext === OverlaysContextsEnum.TRANSPORT_MONITORING ? (
                  <>
                    <TransportMonitoringMap map={map} />
                    <ErrorBoundary message="Unable to render the ground stations">
                      <GroundStationsContainer map={map} />
                    </ErrorBoundary>
                    <ErrorBoundary message="Unable to render the hubs">
                      <HubsContainer map={map} />
                    </ErrorBoundary>
                    <ErrorBoundary message="Unable to render the weather overlay">
                      <WeatherOverlay map={map} />
                    </ErrorBoundary>
                    <OverlaysPanel />
                    <TailTagOverlaysPanel />
                    <MapSettingsPanel />
                    <RulerToolDistanceSummary />
                  </>
                ) : (
                  <div>Could not render</div>
                )}
              </ThemeProvider>
            </StyledEngineProvider>
            <AddTailTagsToMap map={map} isSpaceVehicleTag />
          </React.Fragment>
        )}
      </MapContext.Consumer>
    </BaseMapContainer>
  );
};
export default TransportMonitoringMapContainer;
